import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { 
  Bot, 
  Brain, 
  Shield, 
  Target, 
  Activity,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Zap
} from 'lucide-react';
import { AITradingBot } from './AITradingBot';
import { MarketAnalysisAI } from './MarketAnalysisAI';
import { RiskAssessmentAI } from './RiskAssessmentAI';
import { PortfolioOptimizerAI } from './PortfolioOptimizerAI';

interface AISystemStatus {
  tradingBot: {
    status: 'active' | 'inactive' | 'paused';
    performance: number;
    trades24h: number;
  };
  marketAnalysis: {
    status: 'analyzing' | 'complete' | 'error';
    confidence: number;
    lastUpdate: Date;
  };
  riskAssessment: {
    portfolioRisk: number;
    alerts: number;
    status: 'healthy' | 'warning' | 'critical';
  };
  optimizer: {
    status: 'optimized' | 'needs_rebalance' | 'processing';
    improvement: number;
    lastOptimization: Date;
  };
}

export function AITradingDashboard() {
  const [systemStatus] = useState<AISystemStatus>({
    tradingBot: {
      status: 'active',
      performance: 23.5,
      trades24h: 12
    },
    marketAnalysis: {
      status: 'complete',
      confidence: 87.3,
      lastUpdate: new Date()
    },
    riskAssessment: {
      portfolioRisk: 42,
      alerts: 2,
      status: 'warning'
    },
    optimizer: {
      status: 'needs_rebalance',
      improvement: 8.7,
      lastOptimization: new Date(Date.now() - 24 * 60 * 60 * 1000)
    }
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'complete':
      case 'healthy':
      case 'optimized':
        return 'text-green-500';
      case 'analyzing':
      case 'processing':
      case 'warning':
      case 'needs_rebalance':
        return 'text-yellow-500';
      case 'inactive':
      case 'paused':
      case 'error':
      case 'critical':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
      case 'complete':
      case 'healthy':
      case 'optimized':
        return 'default';
      case 'analyzing':
      case 'processing':
      case 'warning':
      case 'needs_rebalance':
        return 'secondary';
      case 'inactive':
      case 'paused':
      case 'error':
      case 'critical':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
            <Brain className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1>AI Trading Suite</h1>
            <p className="text-muted-foreground">
              Comprehensive AI-powered trading and portfolio management
            </p>
          </div>
        </div>
      </div>

      {/* System Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Trading Bot</p>
                <div className="flex items-center space-x-2">
                  <Badge variant={getStatusBadge(systemStatus.tradingBot.status)}>
                    {systemStatus.tradingBot.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {systemStatus.tradingBot.trades24h} trades today
                </p>
              </div>
              <div className="text-right">
                <Bot className="h-8 w-8 text-blue-500 mb-2" />
                <p className={`text-lg font-bold ${getStatusColor(systemStatus.tradingBot.status)}`}>
                  +{systemStatus.tradingBot.performance}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Market Analysis</p>
                <div className="flex items-center space-x-2">
                  <Badge variant={getStatusBadge(systemStatus.marketAnalysis.status)}>
                    {systemStatus.marketAnalysis.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {systemStatus.marketAnalysis.lastUpdate.toLocaleTimeString()}
                </p>
              </div>
              <div className="text-right">
                <Activity className="h-8 w-8 text-purple-500 mb-2" />
                <p className={`text-lg font-bold ${getStatusColor(systemStatus.marketAnalysis.status)}`}>
                  {systemStatus.marketAnalysis.confidence}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Risk Assessment</p>
                <div className="flex items-center space-x-2">
                  <Badge variant={getStatusBadge(systemStatus.riskAssessment.status)}>
                    {systemStatus.riskAssessment.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {systemStatus.riskAssessment.alerts} active alerts
                </p>
              </div>
              <div className="text-right">
                <Shield className="h-8 w-8 text-orange-500 mb-2" />
                <p className={`text-lg font-bold ${getStatusColor(systemStatus.riskAssessment.status)}`}>
                  {systemStatus.riskAssessment.portfolioRisk}/100
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Portfolio Optimizer</p>
                <div className="flex items-center space-x-2">
                  <Badge variant={getStatusBadge(systemStatus.optimizer.status)}>
                    {systemStatus.optimizer.status.replace('_', ' ')}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Last: {systemStatus.optimizer.lastOptimization.toLocaleDateString()}
                </p>
              </div>
              <div className="text-right">
                <Target className="h-8 w-8 text-green-500 mb-2" />
                <p className={`text-lg font-bold ${getStatusColor(systemStatus.optimizer.status)}`}>
                  +{systemStatus.optimizer.improvement}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Zap className="h-5 w-5" />
            <span>AI Quick Insights</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg bg-green-50 dark:bg-green-900/20">
              <div className="flex items-center space-x-2 mb-2">
                <TrendingUp className="h-4 w-4 text-green-500" />
                <span className="font-semibold text-green-700 dark:text-green-300">
                  Bullish Signal Detected
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                ETH showing strong technical breakout with 87% confidence. Consider increasing position.
              </p>
            </div>

            <div className="p-4 border rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
              <div className="flex items-center space-x-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                <span className="font-semibold text-yellow-700 dark:text-yellow-300">
                  Rebalance Recommended
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Portfolio allocation has drifted 8.7% from optimal. Rebalancing could improve returns.
              </p>
            </div>

            <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <div className="flex items-center space-x-2 mb-2">
                <Activity className="h-4 w-4 text-blue-500" />
                <span className="font-semibold text-blue-700 dark:text-blue-300">
                  High Volume Alert
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Unusual trading volume detected in LINK. Monitoring for potential breakout opportunity.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main AI Trading Components */}
      <Tabs defaultValue="bot" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="bot" className="flex items-center space-x-2">
            <Bot className="h-4 w-4" />
            <span>Trading Bot</span>
          </TabsTrigger>
          <TabsTrigger value="analysis" className="flex items-center space-x-2">
            <Brain className="h-4 w-4" />
            <span>Market Analysis</span>
          </TabsTrigger>
          <TabsTrigger value="risk" className="flex items-center space-x-2">
            <Shield className="h-4 w-4" />
            <span>Risk Assessment</span>
          </TabsTrigger>
          <TabsTrigger value="optimizer" className="flex items-center space-x-2">
            <Target className="h-4 w-4" />
            <span>Portfolio Optimizer</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bot">
          <AITradingBot />
        </TabsContent>

        <TabsContent value="analysis">
          <MarketAnalysisAI />
        </TabsContent>

        <TabsContent value="risk">
          <RiskAssessmentAI />
        </TabsContent>

        <TabsContent value="optimizer">
          <PortfolioOptimizerAI />
        </TabsContent>
      </Tabs>
    </div>
  );
}